import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { OTPVerificationForm } from '@/components/OTPVerificationForm/OTPVerificationForm'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import {
  useOtpVerificationMutation,
  useSendOtpEmailMutation,
} from '@/store/api/Auth/Auth.query'
import { useUserInfoQuery } from '@/store/api/Users/Users.query'
import { trackOnboardingStepViewed, OnboardingSteps } from '@/utils/analytics'

export const EmailVerification = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const hasTrackedView = useRef(false)

  const { data: userData, isSuccess } = useUserInfoQuery()
  const [sendOtpEmail, { isLoading: isLoadingSendEmail }] =
    useSendOtpEmailMutation()
  const [verifyOtpEmail, { isLoading: isLoadingVerifyEmail }] =
    useOtpVerificationMutation()

  // Track step viewed once on mount
  useEffect(() => {
    if (!hasTrackedView.current) {
      trackOnboardingStepViewed(OnboardingSteps.EMAIL_VERIFICATION)
      hasTrackedView.current = true
    }
  }, [])

  const sendEmail = async () => {
    try {
      await sendOtpEmail({
        email: userData?.email || '',
        reason: 'verify_email',
      }).unwrap()
      toast.success(t('feedback.toast.OTPCode'))
    } catch (e) {
      catchWithSentry(e)
      toast.error(t('feedback.toast.errorOTP'))
    }
  }

  const verifyEmail = async (otp: string) => {
    try {
      await verifyOtpEmail({
        email: userData?.email || '',
        reason: 'verify_email',
        otp: parseInt(otp, 10), // Parse to number for the auth endpoint
      }).unwrap()

      toast.success(t('feedback.toast.emailVerified'))
      navigate(AppRoutes.welcomeToSelfCheck)
    } catch (e) {
      catchWithSentry(e)
      toast.error(t('feedback.toast.invalidOTP'))
    }
  }

  useEffect(() => {
    if (userData?.email) sendEmail()
  }, [userData, isSuccess])

  return (
    <WeScreen useHeader={false} customViewStyles="max-md:p-0">
      <div className="flex w-full h-full items-center justify-center">
        <OTPVerificationForm
          email={userData?.email || ''}
          onVerify={verifyEmail}
          onResend={sendEmail}
          isLoadingVerify={isLoadingVerifyEmail}
          isLoadingResend={isLoadingSendEmail}
          resendCooldownSeconds={60}
        />
      </div>
    </WeScreen>
  )
}
